// src/components/cookie-consent/utils.tracking.ts
'use client'

import {IS_GTM_ENABLED, trackingConfig} from './config.tracking'
import {getDeduplicationDebugInfo, recordTrackedEvent, shouldTrackEvent} from './deduplication'

// Extend global types for GTM
declare global {
    interface Window {
        gtag?: (...args: any[]) => void;
        dataLayer?: any[];
        __trackingQueue?: any[];
    }
}

// Helper function to check enhanced analytics consent
const hasEnhancedAnalyticsConsent = (): boolean => {
    try {
        return localStorage.getItem('enhanced_analytics') === 'true';
    } catch {
        return false; // Default to basic analytics if can't access localStorage
    }
};

// Helper to safely push to dataLayer with best practices
const pushToDataLayer = (data: any): void => {
    if (typeof window === 'undefined') return;

    window.dataLayer = window.dataLayer || [];

    // Add timestamp and tracking metadata to all events
    const enrichedData = {
        ...data,
        timestamp: new Date().toISOString(),
        tracking_version: trackingConfig.trackingVersion,
        environment: trackingConfig.environment,
        server_hostname: trackingConfig.serverContainerHostname
    };

    window.dataLayer.push(enrichedData);

    if (trackingConfig.debugMode) {
        // console.log('📊 [GTM DataLayer] Event pushed:', {
// //     event: enrichedData.event || 'unknown',
// //     dataLayerLength: window.dataLayer.length,
// //     environment: trackingConfig.environment,
// //     enhanced_consent: hasEnhancedAnalyticsConsent(),
// //     data: enrichedData
        // });
    }
};

// Queue events if GTM is not ready yet
const queueEvent = (eventCallback: () => void): void => {
    window.__trackingQueue = window.__trackingQueue || [];
    window.__trackingQueue.push(eventCallback);

    if (trackingConfig.debugMode) {
        // console.log('⏳ [GTM Queue] Event queued, total in queue:', window.__trackingQueue.length);
    }
};

// Process queued events when GTM becomes ready
export const processTrackingQueue = (): void => {
    if (!window.__trackingQueue || !window.gtag) return;

    // console.log(`🚀 [GTM Queue] Processing ${window.__trackingQueue.length} queued events`);

    while (window.__trackingQueue.length > 0) {
        const eventCallback = window.__trackingQueue.shift();
        try {
            eventCallback();
        } catch (error) {
            console.error('❌ [GTM Queue] Error processing queued event:', error);
        }
    }
};

// Check if GTM is ready
const isGTMReady = (): boolean => {
    return typeof window !== 'undefined' &&
        typeof window.gtag === 'function' &&
        Array.isArray(window.dataLayer);
};

// Track product detail page view (GA4 ecommerce: view_item)
export const trackViewItem = (productData: {
    item_id: string
    item_name: string
    item_category?: string
    item_brand?: string
    price?: number
    currency?: string
}): void => {
    if (!IS_GTM_ENABLED) return;

    const trackEvent = () => {
        const eventData = {
            currency: productData.currency || 'EUR',
            value: productData.price || 0,
            items: [{
                item_id: productData.item_id,
                item_name: productData.item_name,
                item_category: productData.item_category,
                item_brand: productData.item_brand,
                price: productData.price || 0,
                quantity: 1
            }]
        };

        if (shouldTrackEvent('view_item', eventData, 'gtag') && window.gtag) {
            window.gtag('event', 'view_item', eventData);
            recordTrackedEvent('view_item', eventData, 'gtag');
        }
    };

    if (isGTMReady()) trackEvent();
    else queueEvent(trackEvent);
};

// Track outbound click to shop (affiliate conversion)
export const trackOutboundClick = (data: {
    shop_name: string
    shop_id?: string
    item_id?: string
    item_name?: string
    price?: number
    link_url: string
}): void => {
    if (!IS_GTM_ENABLED) return;

    const trackEvent = () => {
        const eventData = {
            currency: 'EUR',
            value: data.price || 0,
            shop_name: data.shop_name,
            shop_id: data.shop_id,
            link_url: data.link_url,
            items: data.item_id ? [{
                item_id: data.item_id,
                item_name: data.item_name,
                price: data.price || 0,
                quantity: 1
            }] : undefined
        };

        // generate_lead is the standard GA4 event for affiliate/lead generation
        if (shouldTrackEvent('generate_lead', eventData, 'gtag') && window.gtag) {
            window.gtag('event', 'generate_lead', eventData);
            recordTrackedEvent('generate_lead', eventData, 'gtag');
        }
    };

    if (isGTMReady()) trackEvent();
    else queueEvent(trackEvent);
};

// Enhanced product click tracking with best practices
export const trackProductClick = (productData: {
    item_id: string
    item_name: string
    item_category?: string
    item_brand?: string
    price?: number
    currency?: string
    source_page?: string
    source_url?: string
    click_position?: number
    list_name?: string
}): void => {
    
    if (!IS_GTM_ENABLED) {
        if (trackingConfig.debugMode) {
            // console.log('🚫 [GTM Track] Tracking disabled, skipping product click');
        }
        return;
    }

    const trackEvent = () => {
        const enhancedConsent = hasEnhancedAnalyticsConsent();

        // console.log('🛍️ [GTM Track] Preparing product click event:', {
// product_id: productData.item_id,
// product_name: productData.item_name,
// consent_level: enhancedConsent ? 'enhanced' : 'basic',
// environment: trackingConfig.environment,
// server: trackingConfig.serverContainerHostname,
// gtm_ready: isGTMReady()
// });

        // Base event data (always sent for both consent levels)
        const baseEventData = {
            currency: productData.currency || 'EUR',
            value: productData.price || 0,
            privacy_level: enhancedConsent ? 'enhanced' : 'basic',
            items: [{
                item_id: productData.item_id,
                item_name: productData.item_name,
                item_category: productData.item_category,
                item_brand: productData.item_brand,
                price: productData.price || 0,
                quantity: 1
            }]
        };

        // Add enhanced data only if user consented
        const eventData = enhancedConsent ? {
            ...baseEventData,
            source_page: productData.source_page,
            source_url: productData.source_url,
            click_position: productData.click_position,
            list_name: productData.list_name,
            enhanced_analytics_consent: true
        } : {
            ...baseEventData,
            enhanced_analytics_consent: false
        };

        let eventsSent = 0;

        // PRIMARY: Use gtag for standard GA4 ecommerce events
        if (shouldTrackEvent('select_item', eventData, 'gtag')) {
            if (window.gtag) {
                window.gtag('event', 'select_item', eventData);
                recordTrackedEvent('select_item', eventData, 'gtag');
                eventsSent++;
                // console.log('✅ [GTM Track] select_item sent via gtag (GA4 ecommerce)');
            }
        } else {
            // console.log('⚠️ [GTM Track] Skipped gtag select_item - duplicate detected');
        }

        // SECONDARY: Send to dataLayer for custom GTM tags (different event name)
        const customEventData = {
            event: 'custom_product_interaction', // Avoid GA4 reserved names
            ecommerce: {
                currency: eventData.currency,
                value: eventData.value,
                items: eventData.items
            },
            interaction_type: 'product_click',
            product_data: productData,
            consent_level: enhancedConsent ? 'enhanced' : 'basic'
        };

        if (shouldTrackEvent('custom_product_interaction', customEventData, 'dataLayer')) {
            pushToDataLayer(customEventData);
            recordTrackedEvent('custom_product_interaction', customEventData, 'dataLayer');
            eventsSent++;
            // console.log('📊 [GTM Track] Product interaction sent to dataLayer');
        } else {
            // console.log('⚠️ [GTM Track] Skipped dataLayer event - duplicate detected');
        }

        // console.log('🎯 [GTM Track] Product tracking complete:', {
// product_id: productData.item_id,
// consent_level: enhancedConsent ? 'enhanced' : 'basic',
// environment: trackingConfig.environment,
// events_sent: eventsSent,
// deduplication: 'enabled'
// });
    };

    if (isGTMReady()) {
        trackEvent();
    } else {
        // console.log('⏳ [GTM Track] GTM not ready, queuing product click event');
        queueEvent(trackEvent);
    }
};

// Enhanced page view tracking
export const trackPageView = (pageData?: {
    page_path?: string;
    page_title?: string;
    page_category?: string;
    content_group1?: string;
    content_group2?: string;
}): void => {

    if (!IS_GTM_ENABLED) {
        if (trackingConfig.debugMode) {
            // console.log('🚫 [GTM Track] Tracking disabled, skipping page view');
        }
        return;
    }

    const trackEvent = () => {
        const enhancedConsent = hasEnhancedAnalyticsConsent();

        const pageViewData = {
            page_path: pageData?.page_path || window.location.pathname,
            page_title: pageData?.page_title || document.title,
            page_location: window.location.href,
            page_referrer: document.referrer,
            environment: trackingConfig.environment,
            tracking_version: trackingConfig.trackingVersion,
            privacy_level: enhancedConsent ? 'enhanced' : 'basic',
            enhanced_analytics_consent: enhancedConsent,
            ...(pageData?.page_category && {page_category: pageData.page_category}),
            ...(pageData?.content_group1 && {content_group1: pageData.content_group1}),
            ...(pageData?.content_group2 && {content_group2: pageData.content_group2})
        };

        // console.log('📄 [GTM Track] Sending page view:', pageViewData);

        let eventsSent = 0;

        // PRIMARY: Use gtag for GA4 page views (recommended method)
        if (shouldTrackEvent('page_view', pageViewData, 'gtag')) {
            if (window.gtag) {
                window.gtag('event', 'page_view', pageViewData);
                recordTrackedEvent('page_view', pageViewData, 'gtag');
                eventsSent++;
                // console.log('✅ [GTM Track] page_view sent via gtag');
            }
        } else {
            // console.log('⚠️ [GTM Track] Skipped gtag page_view - duplicate detected');
        }

        // SECONDARY: DataLayer for custom tracking (different event name)
        const customPageData = {
            event: 'custom_page_tracking',
            page_data: pageViewData,
            tracking_source: 'manual_call'
        };

        if (shouldTrackEvent('custom_page_tracking', customPageData, 'dataLayer')) {
            pushToDataLayer(customPageData);
            recordTrackedEvent('custom_page_tracking', customPageData, 'dataLayer');
            eventsSent++;
            // console.log('📊 [GTM Track] Custom page tracking sent to dataLayer');
        } else {
            // console.log('⚠️ [GTM Track] Skipped dataLayer page tracking - duplicate detected');
        }
    };

    if (isGTMReady()) {
        trackEvent();
    } else {
        // console.log('⏳ [GTM Track] GTM not ready, queuing page view event');
        queueEvent(trackEvent);
    }
};

// Enhanced conversion tracking
export const trackConversion = (conversionData: {
    event_name: string;
    value?: number;
    currency?: string;
    transaction_id?: string;
    items?: any[];
    custom_parameters?: Record<string, any>;
}): void => {

    if (!IS_GTM_ENABLED) {
        if (trackingConfig.debugMode) {
            // console.log('🚫 [GTM Track] Tracking disabled, skipping conversion');
        }
        return;
    }

    const trackEvent = () => {
        const enhancedConsent = hasEnhancedAnalyticsConsent();

        const eventData = {
            value: conversionData.value || 0,
            currency: conversionData.currency || 'EUR',
            transaction_id: conversionData.transaction_id,
            privacy_level: enhancedConsent ? 'enhanced' : 'basic',
            enhanced_analytics_consent: enhancedConsent,
            environment: trackingConfig.environment,
            ...(conversionData.items && {items: conversionData.items}),
            ...conversionData.custom_parameters
        };

        // console.log('🎯 [GTM Track] Sending conversion event:', {
// event_name: conversionData.event_name,
// value: eventData.value,
// currency: eventData.currency,
// consent_level: enhancedConsent ? 'enhanced' : 'basic'
// });

        let eventsSent = 0;

        // PRIMARY: Use gtag for GA4 conversion events
        if (shouldTrackEvent(conversionData.event_name, eventData, 'gtag')) {
            if (window.gtag) {
                window.gtag('event', conversionData.event_name, eventData);
                recordTrackedEvent(conversionData.event_name, eventData, 'gtag');
                eventsSent++;
                // console.log(`✅ [GTM Track] ${conversionData.event_name} sent via gtag`);
            }
        } else {
            // console.log(`⚠️ [GTM Track] Skipped gtag ${conversionData.event_name} - duplicate detected`);
        }

        // SECONDARY: DataLayer for custom conversion tracking
        const customConversionData = {
            event: `custom_conversion_${conversionData.event_name}`,
            conversion_data: conversionData,
            conversion_value: eventData.value,
            conversion_currency: eventData.currency,
            tracking_source: 'conversion_api'
        };

        if (shouldTrackEvent(`custom_conversion_${conversionData.event_name}`, customConversionData, 'dataLayer')) {
            pushToDataLayer(customConversionData);
            recordTrackedEvent(`custom_conversion_${conversionData.event_name}`, customConversionData, 'dataLayer');
            eventsSent++;
            // console.log(`📊 [GTM Track] Custom conversion ${conversionData.event_name} sent to dataLayer`);
        } else {
            // console.log(`⚠️ [GTM Track] Skipped dataLayer conversion - duplicate detected`);
        }
    };

    if (isGTMReady()) {
        trackEvent();
    } else {
        // console.log('⏳ [GTM Track] GTM not ready, queuing conversion event');
        queueEvent(trackEvent);
    }
};

// Track custom events with best practices
export const trackCustomEvent = (eventName: string, eventData?: Record<string, any>): void => {

    if (!IS_GTM_ENABLED) {
        if (trackingConfig.debugMode) {
            // console.log('🚫 [GTM Track] Tracking disabled, skipping custom event');
        }
        return;
    }

    const trackEvent = () => {
        const enhancedConsent = hasEnhancedAnalyticsConsent();

        const enrichedEventData = {
            ...eventData,
            privacy_level: enhancedConsent ? 'enhanced' : 'basic',
            enhanced_analytics_consent: enhancedConsent,
            environment: trackingConfig.environment
        };

        // console.log(`🔔 [GTM Track] Sending custom event '${eventName}':`, enrichedEventData);

        let eventsSent = 0;

        // PRIMARY: Use gtag for custom events
        if (shouldTrackEvent(eventName, enrichedEventData, 'gtag')) {
            if (window.gtag) {
                window.gtag('event', eventName, enrichedEventData);
                recordTrackedEvent(eventName, enrichedEventData, 'gtag');
                eventsSent++;
                // console.log(`✅ [GTM Track] ${eventName} sent via gtag`);
            }
        } else {
            // console.log(`⚠️ [GTM Track] Skipped gtag ${eventName} - duplicate detected`);
        }

        // SECONDARY: DataLayer for additional custom processing
        const dataLayerEventData = {
            event: `dataLayer_${eventName}`,
            original_event: eventName,
            custom_event_data: eventData,
            tracking_metadata: enrichedEventData
        };

        if (shouldTrackEvent(`dataLayer_${eventName}`, dataLayerEventData, 'dataLayer')) {
            pushToDataLayer(dataLayerEventData);
            recordTrackedEvent(`dataLayer_${eventName}`, dataLayerEventData, 'dataLayer');
            eventsSent++;
            // console.log(`📊 [GTM Track] DataLayer ${eventName} sent`);
        } else {
            // console.log(`⚠️ [GTM Track] Skipped dataLayer ${eventName} - duplicate detected`);
        }
    };

    if (isGTMReady()) {
        trackEvent();
    } else {
        // console.log('⏳ [GTM Track] GTM not ready, queuing custom event');
        queueEvent(trackEvent);
    }
};

// Debug utilities
export const debugGTM = (): void => {
    console.log('==================================================');
    // console.log('🔍 [GTM DEBUG] Current Status');
    console.log('==================================================');
    console.log('Configuration:', trackingConfig);
    console.log('GTM Enabled:', IS_GTM_ENABLED);
    console.log('GTM Ready:', isGTMReady());
    console.log('Enhanced Consent:', hasEnhancedAnalyticsConsent());
    console.log('DataLayer Length:', window.dataLayer?.length || 0);
    console.log('Queued Events:', window.__trackingQueue?.length || 0);

    // Show deduplication info
    const dedupInfo = getDeduplicationDebugInfo();
    console.log('Deduplication Status:', dedupInfo);

    console.log('==================================================');

    // Show recent dataLayer events
    if (window.dataLayer && window.dataLayer.length > 0) {
        console.log('Recent DataLayer events:');
        window.dataLayer.slice(-5).forEach((event, index) => {
            console.log(`  ${index + 1}.`, event.event || 'data', event);
        });
    }

    console.log('==================================================');
    // console.log('💡 [GTM DEBUG] Tips to prevent duplicates:');
    console.log('  1. Use gtag() for standard GA4 events (page_view, select_item, purchase)');
    console.log('  2. Use dataLayer.push() only for custom GTM tags');
    console.log('  3. Avoid sending same event via both methods');
    console.log('  4. Check GTM container for duplicate triggers');
    console.log('==================================================');
};

// Export utilities for external use
export const trackingUtils = {
    trackProductClick,
    trackPageView,
    trackConversion,
    trackCustomEvent,
    debugGTM,
    processTrackingQueue,
    isReady: isGTMReady,
    hasEnhancedConsent: hasEnhancedAnalyticsConsent,
    config: trackingConfig
};
